<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';
import ConfirmComponentPay from '@/components/saju-insight/common/confirmComponent.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import AlertComponent from '@/components/common/Alert.vue';
import { postAnalysisLogAPI } from '@/api/AnalysisApi';
import { useRoute, useRouter } from 'vue-router';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';
import { usePaymentsStore } from '@/store/modules/payments';
import { useTicketProduct } from '@/composables/saju-insight/useTicketProduct';
import { formatNumber } from '@/composables/functions';

const route = useRoute();
const router = useRouter();

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  analysisType: {
    type: Object,
    default: null,
  },
  ticketCountData: {
    type: Object,
    default: null,
  },
  ticketCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'charge', 'payment-success']);

const paymentsStore = usePaymentsStore();
const mobileSajuLnbStore = useMobileSajuLnbStore();
const { fetchTicketProductByTypeId } = useTicketProduct();

/** 분석권 업셀 카드용 상품 정보 ({ boardId, articleId, perUnitPrice, unit, discountPct }) */
const ticketProduct = ref(null);

const guestUserId = computed(() => mobileSajuLnbStore.guestUserId || route.params.id);

const isDialogVisible = ref(props.isVisible);

const isMobile = computed(() => window.ReactNativeWebView);

/** 포인트 직접 구매가가 정가 대비 할인되어 있는지 (pointBasePrice > pointPrice) */
const isPointDiscounted = computed(
  () => Number(props.analysisType?.pointBasePrice) > Number(props.analysisType?.pointPrice),
);

const buyType = ref('');

watch(
  () => props.isVisible,
  async (newVal) => {
    if (newVal) {
      // 분석권이 있으면 결제 방법 선택 UI를 건너뛰고 바로 확인 팝업
      if (props.ticketCount > 0) {
        buyType.value = 'ticket';
        emit('close'); // 부모 상태 초기화 (다음 클릭 시 watcher 재동작 보장)
        await nextTick();
        showConfirmDialog();
        return;
      }
      // 분석권 없으면 포인트 기본 선택
      buyType.value = 'point';
      ticketProduct.value = null;
      await paymentsStore.getOwnPoint();
      // 분석권 업셀 상품은 비동기로 조회 (팝업 표시를 지연시키지 않음)
      fetchTicketProductByTypeId(props.analysisType?.analysisTypeId).then((product) => {
        ticketProduct.value = product;
      });
    }
    isDialogVisible.value = newVal;
  },
);

const closePopup = () => {
  emit('close');
};

const dialogConfrim = ref(null);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref(null);
const warningTxt = ref('');
const dialogAlert = ref(null);

const showConfirmDialog = () => {
  if (buyType.value === 'ticket' && props.ticketCount > 0) {
    // 분석권 직접 사용 확인 팝업
    dialogTitle.value = '분석권을 사용하시겠습니까?';
    dialogTitle2.value = '';
  } else {
    dialogTitle.value = '선택한 결제방법으로';
    dialogTitle2.value = `${props.analysisType?.name}을 시작합니다.`;
  }
  dialogText.value = '사용';
  dialogNextUrl.value = onConfirmPurchase;
  if (buyType.value === 'ticket' && props.ticketCount > 0) {
    warningTxt.value = `${props.analysisType?.name} - 분석권 1건 사용`;
  } else if (buyType.value === 'point') {
    warningTxt.value = `포인트 ${props.analysisType?.pointPrice} P 사용`;
  } else {
    warningTxt.value = '분석권 1건 사용';
  }
  dialogConfrim.value.openConfirmDialog();
};

const onConfirmPurchase = () => {
  emit('charge');
};

const goCharge = () => {
  if (!buyType.value) return;
  isDialogVisible.value = false;
  showConfirmDialog();
};

const isPointActive = ref(false);
const handleBuyPoint = () => {
  buyType.value = 'point';
};

/** 분석권 구매 이동 확인 팝업 ref */
const ticketConfirmRef = ref(null);

/** 업셀 카드 클릭 → 바로 이동하지 않고 작성 내용 초기화 안내 확인 팝업 표시 */
const goTicketProduct = () => {
  ticketConfirmRef.value?.openConfirmDialog();
};

/** 확인(네) 클릭 → 해당 분석권 상품 상세로 이동 (상품 못 찾으면 분석권 목록) */
const moveToTicketProduct = () => {
  if (ticketProduct.value?.articleId) {
    router.push(
      `/purchase/ticket-detail/${ticketProduct.value.boardId}/${ticketProduct.value.articleId}`,
    );
  } else {
    router.push('/purchase/tickets');
  }
  emit('close');
};

const isPointEnough = () => {
  const formattedPoints = paymentsStore?.formattedPoints || '0';
  const points = Number(
    formattedPoints.length >= 4 ? formattedPoints.replace(/,/g, '') : formattedPoints,
  );
  const price = Number(props.analysisType?.pointPrice || 0);

  // 디버깅: pointPrice가 없을 때 상세 로그 출력
  if (!props.analysisType?.pointPrice) {
    console.warn('=== PopAnalysisInfoOther: pointPrice가 없습니다 ===');
    console.warn('props.analysisType 전체:', props.analysisType);
    console.warn('analysisTypeId:', props.analysisType?.analysisTypeId);
    console.warn('pointPrice:', props.analysisType?.pointPrice);
    console.warn('name:', props.analysisType?.name);
    console.warn('props 전체:', props);
  } else {
    console.log('PopAnalysisInfoOther: pointPrice 확인됨', {
      analysisTypeId: props.analysisType?.analysisTypeId,
      name: props.analysisType?.name,
      pointPrice: props.analysisType?.pointPrice,
    });
  }

  isPointActive.value = points >= price;
  return isPointActive.value;
};

onMounted(() => {
  isPointEnough();
});

watch(
  () => [paymentsStore?.formattedPoints, props.analysisType?.pointPrice],
  () => {
    isPointEnough();
  },
  { immediate: true }, // 최초 실행도 포함
);

const onConfirmPayment = async () => {
  const params = {
    guestUserId: guestUserId.value,
    analysisTypeId: props.analysisType?.analysisTypeId,
    paymentMethod: buyType.value,
  };
  try {
    const res = await postAnalysisLogAPI(params);
    if (res.guestUserId) {
      // 결제 성공 시 바로 데이터 갱신 시작
      emit('payment-success', true);
      dialogTitle.value = '분석권이 적용 되었습니다.';
      dialogNextUrl.value = onPaymentSuccessRedirect;
      dialogText.value = '';
      dialogAlert.value.openAlertDialog();
      const cancelBtn = document.querySelector('.v-dialog-alert.active .btn-nor.gray');
      if (cancelBtn) {
        cancelBtn.click();
      }
    }
  } catch (error) {
    const cancelBtn = document.querySelector('.v-dialog-alert.active .btn-nor.gray');
    if (cancelBtn) {
      cancelBtn.click();
    }
    const errorMessage =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      '오류가 발생했습니다.';
    console.log('Error:', error?.response?.data);
    dialogTitle.value = errorMessage;
    dialogText.value = errorMessage;
    dialogAlert.value.openAlertDialog();
  }
};

const onPaymentSuccessRedirect = () => {
  // payment-success는 onConfirmPayment에서 이미 emit됨
  // 팝업 닫을 때는 추가 동작 없음
};
</script>

<template>
  <div v-if="isDialogVisible" class="pop-analysis-info-backdrop">
    <div class="pop-analysis-info" :class="{ mobile: isMobile }">
      <div class="pop-header">
        <div class="pop-title paywall_charge_header_text_title">
          사용하실 결제 방법을 선택해주세요!
        </div>
        <button class="pop-close paywall_charge_header_button_close" @click="closePopup"></button>
      </div>
      <div class="pop-info-row">
        <span class="pop-info-label">보유중</span>
        <div class="pop-info-point-row">
          <span class="pop-info-point">{{ paymentsStore?.formattedPoints || '0' }} P</span>
          <span class="pop-info-divider">|</span>
          <span class="pop-info-ticket">
            <span>{{ `${analysisType?.name || '분석'}권` }}</span>
            <span>{{ `${ticketCount}건` }}</span>
          </span>
        </div>
      </div>
      <div class="pop-options-row">
        <div
          class="option-box point"
          :class="{ active: buyType === 'point', disabled: !isPointActive }"
          @click="() => isPointActive && handleBuyPoint()"
        >
          <div class="option-icon-n"></div>
          <div class="option-desc-col">
            <div class="option-title">포인트 사용하기</div>
            <div class="option-value">
              <template v-if="isPointDiscounted">
                <span class="option-base-price"
                  >{{ formatNumber(analysisType?.pointBasePrice) }} P</span
                >
                <span class="option-price-arrow">→</span>
                <span class="option-price-sale"
                  >{{ formatNumber(analysisType?.pointPrice) }} P</span
                >
              </template>
              <template v-else>{{ formatNumber(analysisType?.pointPrice) }} P</template>
            </div>
          </div>
        </div>
        <div class="option-box ticket-upsell" @click="goTicketProduct">
          <span
            v-if="ticketProduct?.perUnitPrice != null && ticketProduct.discountPct > 0"
            class="upsell-badge"
            >{{ ticketProduct.discountPct }}% 할인</span
          >
          <div class="option-icon-ticket"></div>
          <div class="option-desc-col">
            <div class="option-title">할인 분석권</div>
            <div v-if="ticketProduct?.perUnitPrice != null" class="option-value">
              1{{ ticketProduct.unit }}당 {{ formatNumber(ticketProduct.perUnitPrice) }} P
            </div>
            <div v-else class="option-value upsell-fallback">상품 보기</div>
          </div>
          <span class="upsell-arrow" aria-hidden="true">›</span>
        </div>
      </div>
      <button class="paywall_use_button_confirm" @click="goCharge" :class="{ disabled: !buyType }">
        확인
      </button>
    </div>
    <!-- backdrop 내부에 둬야 같은 스태킹 컨텍스트에서 결제 팝업 위로 표시됨 -->
    <ConfirmComponent
      ref="ticketConfirmRef"
      title="할인 분석권 구매 화면으로 이동하면 현재 작성 중인 내용이 초기화될 수 있습니다."
      title2="이동하시겠습니까?"
      title2-class="confirm-highlight-orange"
      text="네"
      cancel-text="아니오"
      :next-url="moveToTicketProduct"
    />
  </div>

  <ConfirmComponentPay
    ref="dialogConfrim"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="warningTxt"
    :doUrl="onConfirmPayment"
  />

  <!--Alert창-->
  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>

<style scoped>
.pop-analysis-info-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pop-analysis-info {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  width: 400px;
  max-width: calc(100vw - 32px);
  padding: 30px 32px 32px 32px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}
.pop-header {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 18px;
  .pop-close {
    position: absolute;
    right: 16px;
    top: 30px;
  }
}
.pop-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
}
.pop-close {
  background: url('@manseImages/icon_close_1.svg');
  background-size: 24px 24px;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
}
.pop-info-row {
  position: relative;
  display: flex;
  padding: 12px 20px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  background: var(--Gray-200, #f8f8f8);
  color: var(--Gray-700, #575757);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px; /* 128.571% */
  margin-bottom: 12px;
}
.pop-info-label {
  position: absolute;
  left: 20px;
  color: var(--orange-300-main, #eb4c10);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px; /* 150% */
}
.pop-info-point-row {
  display: flex;
  align-items: center;
  width: 100%;
}
.pop-info-point {
  flex: 1;
  text-align: right;
  white-space: nowrap;
}
.pop-info-ticket {
  flex: 1;
  display: flex;
  justify-content: space-between;
  white-space: nowrap;
}
.pop-info-divider {
  color: #d9d9d9;
  margin: 0 8px;
  flex-shrink: 0;
}
.pop-options-row {
  width: 100%;
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
}
.option-box {
  flex: 1;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0 16px 0;
  color: var(--Gray-800, #333);
  gap: 12px;
  cursor: pointer;
  box-sizing: border-box;
  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &.active {
    border: 2px solid var(--orange-300-main, #eb4c10) !important;
    background: var(--Orange-100, #fff8f5);
    margin: -1px;
    color: var(--orange-300-main, #eb4c10);
  }
}
.option-icon-n {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_point.png') no-repeat center/cover;
  margin-bottom: 4px;
}
.option-icon-ticket {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_Frame.png') no-repeat center/cover;
  margin-bottom: 4px;
}
.option-desc-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.option-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px; /* 128.571% */
}
.option-value {
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 133.333% */
  white-space: nowrap;
}
.option-base-price {
  color: var(--Gray-600, #757575);
  font-size: 13px;
  font-weight: 600;
  text-decoration-line: line-through;
}
.option-price-arrow {
  margin: 0 4px;
  color: var(--Gray-500, #949494);
  font-size: 13px;
  font-weight: 600;
}
.option-price-sale {
  color: var(--orange-300-main, #eb4c10);
  font-weight: 700;
}
.option-box.ticket-upsell {
  position: relative;
}
.upsell-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  white-space: nowrap;
}
.upsell-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--Gray-500, #949494);
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
}
.upsell-fallback {
  color: var(--orange-300-main, #eb4c10);
}
.paywall_use_button_confirm {
  display: flex;
  width: 100%;
  max-width: 336px;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  background: var(--orange-300-main, #eb4c10);
  cursor: pointer;
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 133.333% */
  box-sizing: border-box;
  &.disabled {
    background: var(--Gray-500, #949494);
    cursor: not-allowed;
  }
}
.pop-analysis-info.mobile {
  max-width: calc(100% - 32px);
  padding: 20px;
  .pop-header {
    margin-bottom: 20px;
  }
  .pop-close {
    top: 20px;
  }
  .paywall_use_button_confirm {
    width: 100%;
  }
}

@media (max-width: 400px) {
  .pop-analysis-info {
    padding: 20px 16px 20px 16px;
  }
  .pop-analysis-info .pop-header .pop-close {
    top: 20px;
    right: 14px;
  }
  .pop-info-row {
    padding: 10px 12px;
    font-size: 12px;
    line-height: 16px;
  }
  .pop-info-label {
    font-size: 11px;
    flex-shrink: 0;
  }
  .pop-info-point-row {
    white-space: nowrap;
  }
  .pop-info-point,
  .pop-info-ticket {
    font-size: 12px;
  }
  .pop-info-divider {
    margin: 0 6px;
  }
  .option-title {
    font-size: 12px;
    white-space: nowrap;
  }
  .option-value {
    font-size: 15px;
    white-space: nowrap;
  }
  .option-base-price,
  .option-price-arrow {
    font-size: 11px;
  }
  .upsell-badge {
    font-size: 10px;
    top: 6px;
    right: 6px;
  }
  .upsell-arrow {
    right: 6px;
    font-size: 20px;
  }
  .option-icon-n,
  .option-icon-ticket {
    width: 32px;
    height: 32px;
  }
  .option-box {
    padding: 14px 0 12px 0;
    gap: 8px;
  }
  .pop-options-row {
    gap: 8px;
  }
}
</style>
